import { agentCardPhoneNumber } from '../../utilities/formatPhoneNumber';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useClientServiceContext } from 'services/clientServiceProvider';

import { useAuth0 } from '@auth0/auth0-react';
import AddIcon from '@mui/icons-material/Add';

import Heading3 from 'components/Heading3';
import Modal from 'components/Modal';
import ModalWindow from 'components/ModalWindow';
import CloseIcon from 'components/icons/CloseIcon';

import dataLayer from 'utilities/dataLayer';
import { PURL_AGENT_CODE } from 'utilities/sessionStorageKeys';

import image from './image.svg';

import {
    headerIconAlt,
    headerText,
    headerTitle,
    plansButtonLabel
} from './constants';
import {
    setAgentPurl,
    setAgentUserProfile,
    setCheckPreferredAgentModal,
    setIsAgentAssociationCompleted,
    setIsApiFailed,
    setPreferredAgentModal
} from './slice';

import styles from './styles.module.scss';

import { getPurlAgentDataByAgentCode } from 'temp/utilities/apiSession/agentPurl';
import { set } from 'temp/utilities/storage';

const PreferredAgentModal = () => {
    const { clientService } = useClientServiceContext();
    const location = useLocation();
    const history = useHistory();
    const { isAuthenticated } = useAuth0();

    const preferredAgentModal = useSelector(
        state => state.preferredAgentDetails.preferredAgentModal
    );
    const userProfile = useSelector(state => state.userProfile.userProfile);

    const checkPreferredAgentModal = useSelector(
        state => state.preferredAgentDetails.checkPreferredAgentModal
    );

    const agentUserProfile = useSelector(
        state => state.preferredAgentDetails.agentUserProfile
    );
    const agentPurl = useSelector(
        state => state.preferredAgentDetails.agentPurl
    );
    const newPurlAgentCode = useSelector(
        state => state.preferredAgentDetails.agentPurlCode
    );
    const isApiFailed = useSelector(
        state => state.preferredAgentDetails.isApiFailed
    );

    const { dsnpPlan } = useSelector(state => state.enrollDetails);

    const plan = useSelector(({ planDetails }) => planDetails.plan);

    useEffect(() => {
        if (preferredAgentModal) {
            dataLayer('modal_appear', headerTitle);
        }
    }, [preferredAgentModal]);

    const dispatch = useDispatch();
    const [selectedAgent, setSelectedAgent] = useState();

    const getAgentFullName = agent => {
        return `${agent?.agentFirstName || ''} ${
            agent?.agentMiddleName || ''
        } ${agent?.agentLastName || ''}`;
    };

    const getExistingAgentLinkedToConsumer = useCallback(async () => {
        if (!userProfile?.consumerAgentAssociations?.[0]?.agentPurlSwitchedTo) {
            return;
        }
        const fromAgent = await getPurlAgentDataByAgentCode(
            userProfile?.consumerAgentAssociations?.[0]?.agentPurlSwitchedTo
        ).catch(() => {
            dispatch(setIsApiFailed(true));
        });
        if (fromAgent) {
            dispatch(setAgentUserProfile(fromAgent));
        } else {
            dispatch(setAgentUserProfile(null));
        }
    }, [userProfile, dispatch]);

    const onClose = useCallback(() => {
        if (dsnpPlan) {
            dispatch(setPreferredAgentModal(false));
            dispatch(setCheckPreferredAgentModal(false));
            history.push(`${location.pathname}?showThankYouPlanId=${plan?.id}`);
        } else {
            dispatch(setPreferredAgentModal(false));
            dispatch(setCheckPreferredAgentModal(false));
        }
    }, [dispatch, location, plan, dsnpPlan, history]);

    useEffect(() => {
        if (isApiFailed) {
            onClose();
        }
    }, [isApiFailed, onClose]);

    const getAgentByPurlUrlParam = useCallback(async () => {
        if (newPurlAgentCode) {
            const toAgent = await getPurlAgentDataByAgentCode(
                newPurlAgentCode
            ).catch(error => {
                dispatch(setIsApiFailed(true));
            });
            if (toAgent) {
                dispatch(setAgentPurl(toAgent));
                dispatch(setIsApiFailed(false));
            } else {
                dispatch(setAgentPurl(null));
            }
        }
    }, [dispatch, newPurlAgentCode]);

    useEffect(() => {
        getAgentByPurlUrlParam();
    }, [getAgentByPurlUrlParam, newPurlAgentCode]);

    useEffect(() => {
        getExistingAgentLinkedToConsumer();
    }, [userProfile, getExistingAgentLinkedToConsumer]);

    const updateAgentAssociation = useCallback(
        (toAgent, fromAgent) => {
            const payload = {
                agentId: 0,
                agentNpnSwitchedFrom: fromAgent?.agentNpn || '',
                agentNpnSwitchedTo: toAgent?.agentNpn,
                agentPurlSwitchedTo: toAgent?.agentPurlCode,
                consumerId: userProfile?.consumerId,
                isActive: true,
                leadId: null
            };

            clientService
                .agentAssociation(payload)
                .then(() => {
                    dispatch(setIsAgentAssociationCompleted(true));
                    onClose();
                })
                .catch(error => {
                    dispatch(setIsAgentAssociationCompleted(true));
                    onClose();
                });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [userProfile, dispatch, onClose]
    );

    const manageAgentSwitch = useCallback(async () => {
        if (
            agentUserProfile &&
            (agentUserProfile?.agentNpn !== agentPurl?.agentNpn ||
                agentUserProfile?.agentPurlCode !== agentPurl?.agentPurlCode)
        ) {
            dispatch(setPreferredAgentModal(true));
        } else if (!agentUserProfile && agentPurl && isApiFailed) {
            updateAgentAssociation(agentPurl);
        } else if (
            agentUserProfile?.agentNpn === agentPurl?.agentNpn ||
            agentUserProfile?.agentPurlCode === agentPurl?.agentPurlCode
        ) {
            onClose();
        }
    }, [
        agentPurl,
        agentUserProfile,
        dispatch,
        isApiFailed,
        onClose,
        updateAgentAssociation
    ]);

    const onSelectAgent = useCallback(() => {
        dispatch(setPreferredAgentModal(false));
        dispatch(setCheckPreferredAgentModal(false));
        if (agentUserProfile?.agentNpn !== selectedAgent?.agentNpn) {
            updateAgentAssociation(selectedAgent, agentUserProfile);
        } else {
            dispatch(setIsAgentAssociationCompleted(true));
            set(PURL_AGENT_CODE, agentUserProfile?.agentPurlCode);
            onClose();
        }
    }, [
        agentUserProfile,
        dispatch,
        onClose,
        selectedAgent,
        updateAgentAssociation
    ]);

    const AgentWrapper = ({ agent }) => (
        <>
            <div
                className={styles.agentWrapperContainer}
                onClick={() => setSelectedAgent(agent)}
            >
                <div className={styles.agentDetailsContainer}>
                    <Heading3
                        className={styles.heading3}
                        text={getAgentFullName(agent)}
                    />
                    <div className={styles.phoneText}>
                        <span>Phone: </span>
                        {agentCardPhoneNumber(agent?.virtualNumber, true)}
                    </div>
                </div>
                <div className={styles.agentActionContainer}>
                    {agent?.agentNpn === selectedAgent?.agentNpn ? (
                        <CloseIcon
                            className={styles.closeButton}
                            onClick={() => setSelectedAgent()}
                            size={12}
                        />
                    ) : (
                        <AddIcon
                            className={styles.addButton}
                            onClick={() => setSelectedAgent(agent)}
                        />
                    )}
                </div>
            </div>
        </>
    );

    const checkAgentConsumerSwitch = useCallback(() => {
        if (isApiFailed) {
            onClose();
        }

        if (
            agentPurl &&
            isAuthenticated &&
            userProfile?.consumerAgentAssociations &&
            userProfile?.consumerAgentAssociations?.length > 0
        ) {
            manageAgentSwitch();
        } else if (
            agentPurl &&
            isAuthenticated &&
            userProfile &&
            userProfile?.consumerAgentAssociations &&
            userProfile?.consumerAgentAssociations?.length === 0
        ) {
            updateAgentAssociation(agentPurl);
        } else {
            dispatch(setIsAgentAssociationCompleted(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        agentPurl,
        dispatch,
        isApiFailed,
        isAuthenticated,
        manageAgentSwitch,
        onClose,
        updateAgentAssociation,
        userProfile
    ]);

    useEffect(() => {
        if (agentPurl) {
            checkAgentConsumerSwitch();
        }
    }, [
        agentPurl,
        userProfile,
        checkAgentConsumerSwitch,
        checkPreferredAgentModal
    ]);

    return (
        <div className={styles.modalWithHeaderFooter}>
            <Modal className={styles.modal} isOpen={preferredAgentModal}>
                <ModalWindow
                    closeIconClassName={styles.closeIconClassName}
                    contentClassName={styles.contentClassName}
                    footerButtonClassName={styles.footerButtonClassName}
                    footerButtonClicked={onSelectAgent}
                    footerButtonDisabled={!selectedAgent}
                    footerClassName={styles.footerClassName}
                    footerLabel={plansButtonLabel}
                    headerClassName={styles.headerClassName}
                    headerIcon={image}
                    headerIconAlt={headerIconAlt}
                    headerText={headerText}
                    headerTitle={headerTitle}
                    onClose={onClose}
                >
                    <div
                        className={styles.agentContainer}
                        data-testid="your-preferred-agent-modal"
                    >
                        {agentUserProfile?.agentNpn && (
                            <AgentWrapper agent={agentUserProfile} />
                        )}
                        {agentPurl?.agentNpn && (
                            <AgentWrapper agent={agentPurl} />
                        )}
                    </div>
                </ModalWindow>
            </Modal>
        </div>
    );
};

export default PreferredAgentModal;
